import { useState } from 'react';
import {
  Button, ListboxStyled, ListboxStyledButton, ListboxStyledOptions, ListboxStyledOption,
} from '@components/common';
import { ApiStaticIdName } from '@type/v1-api-types/static';
import { SelectedLanguageParam } from '@type/filters';
import { ArrowRightCircleIcon } from '@heroicons/react/24/solid';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';


// Interface
// **********************************************
interface LanguagePillProps {
  language?: SelectedLanguageParam;
  languageLevels: ApiStaticIdName[];
  changeLanguageLevel: (languageId: string, languageLevelId: string, languageLevelName: string) => void;
  deleteLanguage: (languageId: string) => void;
}


/** @description The language pill component.
 * * **language** - the language object.
 * * **languageLevels** - the language levels list.
 * * **changeLanguageLevel** - the state setter for the selected languages. Changes the language level.
 * * **deleteLanguage** - the state setter for the selected languages. Deletes the language.
 */
export const LanguagePill = (props: LanguagePillProps) => {
  // Destructure props
  const {
    language,
    languageLevels,
    changeLanguageLevel,
    deleteLanguage,
  } = props;


  // Helper to get the initial language level
  const initialLanguageLevel = languageLevels.find((level) => level.id === language?.level) || languageLevels[0];


  // Store the listbox selected language level
  // **********************************************
  const [
    selectedLanguageLevel, setSelectedLanguageLevel,
  ] = useState<ApiStaticIdName>(initialLanguageLevel);


  // Return nothing if there is no language
  // **********************************************
  if (!language) return null;


  // Render component
  // **********************************************
  return (
    <div className="inline-flex items-center gap-1.5 rounded-full border border-input px-3 py-1">
      {/* Icon */}
      <ArrowRightCircleIcon className="size-5" />

      {/* Language name */}
      <span>{`${language?.name} -`}</span>

      {/* Language levels dropdown */}
      <ListboxStyled
        value={selectedLanguageLevel}
        onChange={setSelectedLanguageLevel}
        by="name"
      >
        <ListboxStyledButton
          as={Button}
          styling="text"
          color="primary-light"
          className="outline-0"
        >
          {selectedLanguageLevel.name}
          <ChevronDownIcon className="ml-1.5 size-4 stroke-2" />
        </ListboxStyledButton>

        <ListboxStyledOptions
          className="mt-1 min-w-48 divide-y overflow-hidden !rounded-md"
          animation="slideDown"
          position="left"
        >
          {languageLevels.map((level) => (
            <ListboxStyledOption
              key={`option_${level.id}` || 'option_all_levels'}
              value={level}
            >
              <Button
                styling="none"
                className="w-full px-3 py-2 text-left text-ink-medium hover:bg-primary-light hover:text-white data-selected:text-primary-light"
                onClick={() => changeLanguageLevel(language.id, level.id, level.name)}
              >
                {level.name}
              </Button>
            </ListboxStyledOption>
          ))}
        </ListboxStyledOptions>
      </ListboxStyled>

      {/* Remove language button */}
      <Button
        styling="none"
        className="-mr-1.5 ml-2.5 flex size-6 items-center justify-center rounded-full bg-surface-100 text-ink/70 transition-all duration-200 hover:bg-error hover:text-surface"
        onClick={() => deleteLanguage(language.id)}
      >
        <XMarkIcon className="size-3 stroke-[3px]" />
      </Button>
    </div>
  );
};
